import React, { useState, useEffect, useCallback } from 'react';
import { Card, Typography, Skeleton, message, Tabs, Button, Select } from 'antd';
import { getAllApplications, addGradeToApplication, updateApplicationRounds } from '../../api/application';
import { fetchProvinces } from '../../api/competition';
import { useSettings } from '../../contexts/settingsContext';
import { getVenues } from '../../api/venue';
import { loadState } from '../../stores/store';
import FileInput from './FileImport';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import debounce from 'lodash/debounce';
import { generateGradingSheetPDF, exportGradedApplicationsToXLSX } from '../../utils/gradingUtils';
import GradingTable from '../../components/Grading/gradingTable';
import MedalWinnersModal from '../../components/Grading/medalWinnersModal';
import './grading.scss';

dayjs.extend(utc);
dayjs.extend(timezone);

const { Title } = Typography;

const Grading = () => {
    const { state: settings } = useSettings();
    const [applications, setApplications] = useState([]);
    const [loading, setLoading] = useState(true);
    const [venues, setVenues] = useState([]);
    const [selectedVenue, setSelectedVenue] = useState(null);
    const [grades, setGrades] = useState({});
    const [gradingFinished, setGradingFinished] = useState(false);
    const [provinces, setProvinces] = useState([]);
    const [provinceFilter, setProvinceFilter] = useState(null);

    const state = loadState();
    const user = state.user;

    const [medalWinnersVisible, setMedalWinnersVisible] = useState(false);

    const medalTitleMap = {
        'gold-row': 'Gold',
        'silver-row': 'Silver',
        'bronze-row': 'Bronze'
    };

    const mapSizeToType = (size) => {
        if (size === 2) return 'duet';
        if (size === 3) return 'trio';
        if (size >= 4 && size <= 9) return 'small group';
        if (size >= 10 && size <= 19) return 'large group';
        if (size >= 20) return 'production line';
        return 'solo';
    };

    const fetchApplications = async () => {
        try {
            const applicationsData = await getAllApplications(settings.selectedRound);
            const currentApplications = applicationsData
                .filter((app) => app.round === settings.selectedRound)
                .sort((a, b) => new Date(a.date) - new Date(b.date)); // Sort by date 
            
            const fetchedProvinces = await fetchProvinces(settings.selectedCompetition._id);
            setProvinces(fetchedProvinces);

            let filteredApplications = currentApplications;

            if (provinceFilter && provinceFilter !== "all") {
                filteredApplications = currentApplications.filter(
                    a => a.province === provinceFilter
                );
            }

            // Pre-populate grades  
            const initialGrades = {};
            filteredApplications.forEach((app) => {
                const userGrades = app.grades.find((grade) => grade.graded_by === state.user._id);
                if (userGrades) {
                    initialGrades[app._id] = userGrades;
                }
            });

            setGrades(initialGrades);
            setApplications(filteredApplications);
            setLoading(false);

            // Log array with every application ID to the console  
            const applicationIds = currentApplications.map(app => app._id);

        } catch (error) {
            console.error(`Failed to load applications: ${error}`);
            message.error('Failed to load applications.');
            setLoading(false);
        }
    };

    const fetchVenues = async () => {
        try {
            const venuesData = await getVenues();
            setVenues(venuesData);
            if (venuesData.length > 0) {
                setSelectedVenue(venuesData[0].venue); // Default to the first venue    
            }
        } catch (error) {
            message.error('Failed to load venues.');
        }
    };

    useEffect(() => {
        fetchApplications();
        fetchVenues();
    }, [settings.selectedRound, state.user._id, provinceFilter]);

    const handleTabChange = (venue) => {
        setSelectedVenue(venue);
    };

    const debouncedAddGrade = useCallback(
        debounce(async (applicationId, grades) => {
            try {
                await addGradeToApplication(applicationId, grades);

                // Update the applications state to include the new grade    
                setApplications(prevApplications =>
                    prevApplications.map(app => {
                        if (app._id === applicationId) {
                            // Replace any existing grade by the current user    
                            const existingGrades = app.grades.filter(grade => grade.graded_by !== state.user._id);
                            return {
                                ...app,
                                grades: [...existingGrades, { ...grades, graded_by: state.user._id }]
                            };
                        }
                        return app;
                    })
                );

                message.success('Grade added successfully');
            } catch (error) {
                console.error('Error adding grade:', error);
                message.error('Failed to add grade');
            }
        }, 500),
        [state.user._id]
    );

    const handleGradeChange = (applicationId, field, value) => {
        setGrades((prevGrades) => {
            const updatedGrades = { ...prevGrades, [applicationId]: { ...prevGrades[applicationId], [field]: value } };
            const applicationGrades = updatedGrades[applicationId];
            const requiredFields = ['entertainment', 'technicality', 'style', 'stage_presence', 'originality'];
            const allFieldsFilled = requiredFields.every((field) => applicationGrades[field] !== undefined);
            if (allFieldsFilled) {
                debouncedAddGrade(applicationId, applicationGrades);
            }
            return updatedGrades;
        });
    };

    const calculateFinalGrade = (application) => {
        const applicationGrades = grades[application._id] || {};
        const requiredFields = ['entertainment', 'technicality', 'style', 'stage_presence', 'originality'];
        return requiredFields.reduce((sum, field) => sum + (applicationGrades[field] || 0), 0);
    };

    const calculateAverageFinalGrade = (application) => {
        const applicationGrades = application.grades || [];
        if (applicationGrades.length === 0) return null;
        const criteria = ['entertainment', 'technicality', 'style', 'stage_presence', 'originality'];
        // Calculate average for each criterion    
        const criteriaAverages = criteria.map((criterion) => {
            const total = applicationGrades.reduce((acc, grade) => acc + (grade[criterion] || 0), 0);
            return total / applicationGrades.length;
        });
        // Sum up the averages of all criteria    
        const totalAverageGrade = criteriaAverages.reduce((sum, avg) => sum + avg, 0);
        return totalAverageGrade;
    };

    const filteredApplications = applications.filter(
        (app) => app.event && app.event.venue && app.event.venue.venue === selectedVenue
    );

    const handleExportGradedApplications = () => {
        const gradedApplications = filteredApplications.filter(app => {
            const hasSavedGrades = app.grades && app.grades.length > 0;
            const hasLocalGrades = grades[app._id] && Object.keys(grades[app._id]).length > 0;
            return hasSavedGrades || hasLocalGrades;
        });
        exportGradedApplicationsToXLSX(gradedApplications, grades, selectedVenue, user, gradingFinished, medalAssignments);
    };

    const groupByEventStyle = (applications) => {
        return applications.reduce((acc, app) => {
            const eventStyle = app.event.style;
            if (!acc[eventStyle]) {
                acc[eventStyle] = [];
            }
            acc[eventStyle].push(app);
            return acc;
        }, {});
    };

    // Group applications by event style    
    const groupedApplications = groupByEventStyle(filteredApplications);

    // Compute medal assignments    
    const medalAssignments = {};
    Object.keys(groupedApplications).forEach(eventStyle => {
        const styleGroup = groupedApplications[eventStyle];
        const sortedStyleGroup = [...styleGroup].sort(
            (a, b) => calculateAverageFinalGrade(b) - calculateAverageFinalGrade(a)
        );
        // Variables to track medal awarding    
        let medalAwarded = null;
        // Iterate over the top 3 performers    
        for (let i = 0; i < sortedStyleGroup.length && i < 3; i++) {
            const application = sortedStyleGroup[i];
            const avgGrade = calculateAverageFinalGrade(application);
            if (i === 0) {
                if (avgGrade >= settings.gradingDetails.goldMedalGrade) {
                    medalAssignments[application._id] = 'gold-row';
                    medalAwarded = 'gold';
                } else {
                    // No gold medal awarded    
                    medalAwarded = null;
                }
            } else if (i === 1) {
                if (medalAwarded === 'gold' && avgGrade >= settings.gradingDetails.silverMedalGrade) {
                    medalAssignments[application._id] = 'silver-row';
                    medalAwarded = 'silver';
                } else {
                    // No silver medal awarded    
                    medalAwarded = null;
                }
            } else if (i === 2) {
                if (medalAwarded === 'silver' && avgGrade >= settings.gradingDetails.bronzeMedalGrade) {
                    medalAssignments[application._id] = 'bronze-row';
                    medalAwarded = 'bronze';
                } else {
                    // No bronze medal awarded    
                }
            }
        }
    });

    // Function to determine the row class name based on passing grade    
    const getRowClassName = (record) => {
        const averageFinalGrade = calculateAverageFinalGrade(record);
        if (averageFinalGrade === null) return '';
        return averageFinalGrade >= settings.gradingDetails.passingGrade ? 'pass-row' : 'fail-row';
    };

    const getMedalWinners = () => {
        const medalWinners = [];
        Object.keys(medalAssignments).forEach(applicationId => {
            const application = filteredApplications.find(app => app._id === applicationId);
            if (application) {
                let contestantName;
                if (application.contestant) {
                    contestantName = `${application.contestant.first_name} ${application.contestant.last_name}`;
                } else if (application.group) {
                    contestantName = application.group.group_name;
                }
                medalWinners.push({
                    ...application,
                    medal: medalTitleMap[medalAssignments[applicationId]],
                    contestantName: contestantName // Add the full name(s) here    
                });
            }
        });
        return medalWinners;
    };

    const medalWinners = getMedalWinners();

    const handleDuplicatePassedApplications = async () => {
        setLoading(true);
        try {
            // Get all applications that have passed  
            const passedApplications = filteredApplications.filter((app) => {
                const avgGrade = calculateAverageFinalGrade(app);
                return avgGrade !== null && avgGrade >= settings.gradingDetails.passingGrade;
            });

            // Extract their IDs  
            const applicationIds = passedApplications.map((app) => app._id);

            if (applicationIds.length === 0) {
                message.info('No passing applications.');
                setLoading(false);
                return;
            }

            // Call the API to duplicate applications  
            await updateApplicationRounds(applicationIds);

            message.success('Passing applications successfully moved.');

            // Optionally, you can refresh the applications list here  
            // fetchApplications();  
        } catch (error) {
            console.error('Error duplicating applications:', error);
            message.error('Failed to duplicate passed applications.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="schedule-container">
            <Title level={4}>Grading</Title>
            <Card bordered={false} className="applications-card" style={{ position: 'relative' }}>
                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 16, marginBottom: 16 }}>
                    <Select
                        value={provinceFilter}
                        onChange={value => setProvinceFilter(value)}
                        style={{ width: 250 }}
                        showSearch
                        allowClear
                        placeholder="Select a province"
                    >
                        <Select.Option value="">All</Select.Option>
                        {provinces && Array.isArray(provinces) &&
                            provinces.map(prov => (
                                <Select.Option key={prov._id} value={prov._id}>
                                    {prov.province} ({prov.location}) - {prov.date}
                                </Select.Option>
                            ))
                        }
                    </Select>
                </div>
                {loading ? (
                    <Skeleton active />
                ) : (
                    <>
                        <Tabs defaultActiveKey={selectedVenue} onChange={handleTabChange}>
                            {venues.map((venue) => (
                                <Tabs.TabPane tab={venue.venue} key={venue.venue}></Tabs.TabPane>
                            ))}
                        </Tabs>
                        <Button
                            type="primary"
                            style={{ marginBottom: 16 }}
                            onClick={() => generateGradingSheetPDF(filteredApplications, selectedVenue)}
                        >
                            Generate Grading Sheet PDF
                        </Button>
                        <Button
                            type="primary"
                            style={{ marginLeft: 8 }}
                            onClick={handleExportGradedApplications}
                        >
                            Export Applications to XLSX
                        </Button>
                        <Button
                            type="primary"
                            style={{ marginLeft: 8 }}
                            onClick={() => setMedalWinnersVisible(true)}
                        >
                            Show Medal Winners
                        </Button>
                        <Button
                            type="primary"
                            style={{ marginLeft: 8 }}
                            onClick={handleDuplicatePassedApplications}
                        >
                            Move Passing Applications to Next Round
                        </Button>
                        <GradingTable
                            filteredApplications={filteredApplications}
                            getRowClassName={(record) => {
                                if (!gradingFinished) {
                                    return '';
                                }
                                const baseClassName = getRowClassName(record); // 'pass-row' or 'fail-row'    
                                const medalClassName = medalAssignments[record._id] || '';
                                return `${baseClassName} ${medalClassName}`.trim();
                            }}
                            gradingFinished={gradingFinished}
                            grades={grades}
                            handleGradeChange={handleGradeChange}
                            userRole={state.user?.role}
                            calculateFinalGrade={calculateFinalGrade}
                            calculateAverageFinalGrade={calculateAverageFinalGrade}
                            mapSizeToType={mapSizeToType}
                            settings={settings}
                            medalWinners={medalWinners}
                        />
                        <MedalWinnersModal
                            visible={medalWinnersVisible}
                            onCancel={() => setMedalWinnersVisible(false)}
                            medalWinners={medalWinners}
                            gradingFinished={gradingFinished}
                        />
                    </>
                )}
            </Card>
        </div>
    );
};

export default Grading;  